import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, X, Zap, GitBranch, ListOrdered, AlertTriangle } from 'lucide-react';

const ConfirmRunAgentsModal = ({ isOpen, onClose, onConfirm, jobs = [] }) => {
    const [selectedMode, setSelectedMode] = useState('sequential');

    const handleConfirm = () => {
        onConfirm(selectedMode);
        onClose();
    };

    const handleClose = () => {
        setSelectedMode('sequential');
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div
                    className="fixed inset-0 z-[1000] flex items-center justify-center p-6"
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.82)' }}
                    onClick={handleClose}
                >
                    {/* Two-panel container — drawer + card side by side */}
                    <div
                        className="relative flex w-full max-w-[620px] max-h-[80vh] rounded-xl shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* ── Left drawer: task list ── */}
                        <motion.div
                            initial={{ opacity: 0, x: -32 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -32 }}
                            transition={{ duration: 0.22, delay: 0.07, ease: 'easeOut' }}
                            className="flex-1 flex flex-col rounded-l-xl border border-r-0 border-border-color overflow-hidden"
                            style={{ backgroundColor: 'rgb(var(--background-dark-rgb))' }}
                        >
                            <div className="px-4 pt-4 pb-3 border-b border-border-color flex-shrink-0">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-dim">
                                    {jobs.length} Task{jobs.length !== 1 ? 's' : ''} Queued
                                </p>
                            </div>
                            <div className="flex-1 overflow-y-auto p-3 space-y-2">
                                {jobs.map((job, i) => (
                                    <div key={job.id} className="flex items-start gap-2.5">
                                        <span className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full bg-surface-dark border border-border-color flex items-center justify-center text-[9px] font-bold text-on-surface-dim">
                                            {i + 1}
                                        </span>
                                        <p className="text-[11px] text-on-surface-dim leading-snug line-clamp-2" title={job.content}>{job.content}</p>
                                    </div>
                                ))}
                            </div>
                        </motion.div>

                        {/* ── Right card: mode select + confirm ── */}
                        {/* shadow casts leftward over the drawer so the card feels "on top" */}
                        <motion.div
                            initial={{ opacity: 0, y: -16, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 16, scale: 0.97 }}
                            transition={{ duration: 0.18, ease: 'easeOut' }}
                            className="flex-1 flex flex-col rounded-r-xl border border-border-color z-10 overflow-hidden"
                            style={{ backgroundColor: 'rgb(var(--surface-rgb))', boxShadow: '-4px 0 12px rgba(0,0,0,0.12)' }}
                        >
                            {/* Header */}
                            <div className="flex items-start gap-3 px-5 pt-5 pb-4">
                                <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-primary/15 border border-primary/30">
                                    <Bot size={17} className="text-primary" />
                                </div>
                                <div className="flex-1 pt-0.5">
                                    <h3 className="text-sm font-bold text-on-surface leading-tight">
                                        Launch {jobs.length} Agent{jobs.length !== 1 ? 's' : ''}
                                    </h3>
                                    <p className="text-[11px] text-on-surface-dim mt-0.5 leading-relaxed">
                                        Each task is a full autonomous agent turn. Choose how they run.
                                    </p>
                                </div>
                                <button
                                    onClick={handleClose}
                                    className="flex-shrink-0 p-1 rounded-full text-on-surface-dim hover:bg-surface-dark transition-colors"
                                >
                                    <X size={14} />
                                </button>
                            </div>

                            {/* Mode selection */}
                            <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-2">
                                {/* Sequential */}
                                <button
                                    type="button"
                                    onClick={() => setSelectedMode('sequential')}
                                    className={`w-full flex items-start gap-3 rounded-xl border p-3.5 text-left transition-all ${
                                        selectedMode === 'sequential'
                                            ? 'border-primary bg-primary/10'
                                            : 'border-border-color bg-background-dark hover:border-primary/40'
                                    }`}
                                >
                                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${selectedMode === 'sequential' ? 'bg-surface-dark' : 'bg-surface-dark'}`}>
                                        <ListOrdered size={15} className={selectedMode === 'sequential' ? 'text-primary' : 'text-on-surface-dim'} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-xs font-semibold ${selectedMode === 'sequential' ? 'text-on-surface' : 'text-on-surface-dim'}`}>
                                            One at a Time
                                        </p>
                                        <span className="text-[9px] uppercase tracking-wide text-primary font-semibold mt-1 block">Recommended</span>
                                        <p className="text-[10px] text-on-surface-dim mt-1.5 leading-relaxed">
                                            Each agent sees the previous one's work. Best when tasks build on each other.
                                        </p>
                                    </div>
                                    <div className={`flex-shrink-0 mt-0.5 w-3.5 h-3.5 rounded-full border-2 transition-colors ${
                                        selectedMode === 'sequential' ? 'border-primary bg-primary' : 'border-border-color'
                                    }`} />
                                </button>

                                {/* Parallel */}
                                <button
                                    type="button"
                                    onClick={() => setSelectedMode('parallel')}
                                    className={`w-full flex items-start gap-3 rounded-xl border p-3.5 text-left transition-all ${
                                        selectedMode === 'parallel'
                                            ? 'border-amber-400/60 bg-amber-400/[0.07]'
                                            : 'border-border-color bg-background-dark hover:border-amber-400/30'
                                    }`}
                                >
                                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${selectedMode === 'parallel' ? 'bg-surface-dark' : 'bg-surface-dark'}`}>
                                        <GitBranch size={15} className={selectedMode === 'parallel' ? 'text-amber-400' : 'text-on-surface-dim'} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-xs font-semibold ${selectedMode === 'parallel' ? 'text-on-surface' : 'text-on-surface-dim'}`}>
                                            All at Once
                                        </p>
                                        <p className="text-[10px] text-on-surface-dim mt-0.5 leading-relaxed">
                                            Every agent starts fresh simultaneously. Use only when tasks are fully independent.
                                        </p>
                                    </div>
                                    <div className={`flex-shrink-0 mt-0.5 w-3.5 h-3.5 rounded-full border-2 transition-colors ${
                                        selectedMode === 'parallel' ? 'border-amber-400 bg-amber-400' : 'border-border-color'
                                    }`} />
                                </button>

                                <AnimatePresence>
                                    {selectedMode === 'parallel' && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.15 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="flex items-start gap-2 rounded-lg bg-amber-400/10 border border-amber-400/30 px-3 py-2.5">
                                                <AlertTriangle size={12} className="flex-shrink-0 mt-0.5 text-amber-400" />
                                                <p className="text-[10px] text-amber-300 leading-relaxed">
                                                    Agents won't see each other's output. If tasks depend on each other, use <strong>One at a Time</strong>.
                                                </p>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end gap-2.5 px-5 py-3.5 bg-background-dark border-t border-border-color">
                                <button
                                    onClick={handleClose}
                                    className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-surface hover:bg-surface-raised text-on-surface transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    className={`px-4 py-1.5 text-xs font-bold rounded-lg text-white transition-colors flex items-center gap-1.5 ${
                                        selectedMode === 'parallel'
                                            ? 'bg-amber-500 hover:bg-amber-400'
                                            : 'bg-primary hover:bg-primary-dark'
                                    }`}
                                >
                                    <Zap size={12} />
                                    Launch {jobs.length} Agent{jobs.length !== 1 ? 's' : ''}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default ConfirmRunAgentsModal;
